import React from 'react';
import { useLazyImageLoad } from '../hooks/useLazyImageLoad.ts';
import { useResponsiveMargin } from '../hooks/useResponsiveMargin.ts';
import './LazyProductImage.css';

interface LazyProductImageProps {
  src: string | undefined;
  alt: string;
  className?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

const LazyProductImage: React.FC<LazyProductImageProps> = ({
  src,
  alt,
  className = '',
  onError
}) => {
  // Responsive preloading: less aggressive on mobile
  const rootMargin = useResponsiveMargin();

  const {
    imgRef,
    imageSrc,
    imageLoaded,
    imageError,
    handleImageLoad,
    handleImageError
  } = useLazyImageLoad(src, {
    rootMargin, // Mobile: 800px, Desktop: 1500px 
    threshold: 0.01
  });

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    handleImageError();
    if (onError) {
      onError(e);
    }
  };

  return (
    <div className={`lazy-image-wrapper ${className}`}>
      {!imageLoaded && !imageError && (
        <div className="image-shimmer">
          <div className="shimmer-animation"></div>
        </div>
      )}
      
      {/* Only render img tag when we have a real src to avoid empty src errors */}
      {imageSrc && (
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          className={`product-image ${imageLoaded ? 'image-loaded' : ''} ${imageError ? 'error' : ''}`}
          onLoad={handleImageLoad}
          onError={handleError}
          loading="lazy"
          draggable={false}
        />
      )}

      {/* Invisible img element for intersection observer when no src yet */}
      {!imageSrc && (
        <img
          ref={imgRef}
          alt=""
          style={{ opacity: 0, position: 'absolute', pointerEvents: 'none' }}
          draggable={false}
        />
      )}
      
      {imageError && (
        <div className="image-error-placeholder">
          <span>Image unavailable</span>
        </div>
      )}
    </div>
  );
};

export default LazyProductImage;